import { Helmet } from 'react-helmet-async'
import { Shield, Lock, Eye, FileText, Mail, Calendar } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const Privacy = () => {
  const { language } = useLanguage()

  const content = {
    tr: {
      title: 'Gizlilik Politikası',
      lastUpdated: 'Son Güncelleme: 6 Kasım 2024',
      sections: [
        {
          icon: Shield,
          title: 'Kişisel Verilerin Korunması',
          content: `Thinklyze olarak, kişisel verilerinizin korunmasına büyük önem veriyoruz. Bu Gizlilik Politikası, web sitemizi ziyaret ettiğinizde veya hizmetlerimizi kullandığınızda topladığımız bilgilerin nasıl toplandığını, kullanıldığını, paylaşıldığını ve korunduğunu açıklamaktadır.`,
        },
        {
          icon: FileText,
          title: 'Toplanan Bilgiler',
          content: `Web sitemizi kullanırken aşağıdaki bilgileri toplayabiliriz:
          
• İletişim bilgileri (ad, soyad, e-posta, telefon numarası)
• İş bilgileri (şirket adı, pozisyon)
• Web sitesi kullanım verileri (IP adresi, tarayıcı türü, sayfa görüntülemeleri)
• Çerezler ve benzeri teknolojiler aracılığıyla toplanan veriler`,
        },
        {
          icon: Eye,
          title: 'Bilgilerin Kullanımı',
          content: `Topladığımız bilgileri aşağıdaki amaçlarla kullanırız:

• Hizmetlerimizi sunmak ve iyileştirmek
• İletişim taleplerinize yanıt vermek
• Pazarlama ve promosyon faaliyetleri (izin verdiğiniz takdirde)
• Yasal yükümlülüklerimizi yerine getirmek
• Web sitesi performansını analiz etmek ve iyileştirmek`,
        },
        {
          icon: Lock,
          title: 'Bilgilerin Paylaşımı',
          content: `Kişisel verilerinizi üçüncü taraflarla paylaşmıyoruz, ancak aşağıdaki durumlarda paylaşım yapılabilir:

• Hizmet sağlayıcılarımız (hosting, e-posta servisleri)
• Yasal zorunluluklar gereği yetkili makamlar
• İş ortaklarımız (açık rızanızla)
• Şirket birleşmeleri veya devir durumlarında`,
        },
        {
          icon: Shield,
          title: 'Veri Güvenliği',
          content: `Verilerinizin güvenliğini sağlamak için endüstri standardı güvenlik önlemleri alıyoruz:

• SSL/TLS şifreleme
• Güvenli sunucu altyapısı
• Düzenli güvenlik denetimleri
• Erişim kontrolleri ve yetkilendirme`,
        },
        {
          icon: Calendar,
          title: 'Veri Saklama Süresi',
          content: `Kişisel verileriniz, toplama amacının gerektirdiği süre boyunca saklanır. Yasal saklama süreleri dolduğunda veya verilerin artık gerekli olmadığında, güvenli bir şekilde silinir veya anonimleştirilir.`,
        },
        {
          icon: Mail,
          title: 'Haklarınız',
          content: `KVKK kapsamında aşağıdaki haklara sahipsiniz:

• Kişisel verilerinizin işlenip işlenmediğini öğrenme
• İşlenen verileriniz hakkında bilgi talep etme
• Verilerinizin düzeltilmesini veya silinmesini isteme
• İşleme faaliyetlerine itiraz etme
• Verilerinizin aktarılmasını talep etme
• İşleme faaliyetine rıza gösterdiyseniz rızanızı geri çekme`,
        },
      ],
      contact: {
        title: 'İletişim',
        description: 'Gizlilik politikamız hakkında sorularınız varsa veya haklarınızı kullanmak istiyorsanız bizimle iletişime geçebilirsiniz:',
        email: 'info@thinklyze.com',
        address: 'Zeytinlik Mah. Fişekhane Cad. Carousel No: 5 İç Kapı No: 17 Bakırköy/İstanbul',
      },
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated: November 6, 2024',
      sections: [
        {
          icon: Shield,
          title: 'Protection of Personal Data',
          content: `At Thinklyze, we place great importance on the protection of your personal data. This Privacy Policy explains how we collect, use, share, and protect information when you visit our website or use our services.`,
        },
        {
          icon: FileText,
          title: 'Information Collected',
          content: `When using our website, we may collect the following information:
          
• Contact information (name, surname, email, phone number)
• Business information (company name, position)
• Website usage data (IP address, browser type, page views)
• Data collected through cookies and similar technologies`,
        },
        {
          icon: Eye,
          title: 'Use of Information',
          content: `We use the information we collect for the following purposes:

• To provide and improve our services
• To respond to your contact requests
• Marketing and promotional activities (with your consent)
• To fulfill our legal obligations
• To analyze and improve website performance`,
        },
        {
          icon: Lock,
          title: 'Sharing of Information',
          content: `We do not share your personal data with third parties, except in the following cases:

• Our service providers (hosting, email services)
• Authorized authorities as required by law
• Our business partners (with your explicit consent)
• In case of company mergers or transfers`,
        },
        {
          icon: Shield,
          title: 'Data Security',
          content: `We implement industry-standard security measures to ensure the security of your data:

• SSL/TLS encryption
• Secure server infrastructure
• Regular security audits
• Access controls and authorization`,
        },
        {
          icon: Calendar,
          title: 'Data Retention Period',
          content: `Your personal data is retained for as long as necessary for the purpose of collection. When legal retention periods expire or data is no longer needed, it is securely deleted or anonymized.`,
        },
        {
          icon: Mail,
          title: 'Your Rights',
          content: `Under GDPR/KVKK, you have the following rights:

• Learn whether your personal data is being processed
• Request information about your processed data
• Request correction or deletion of your data
• Object to processing activities
• Request transfer of your data
• Withdraw your consent if you have given consent for processing`,
        },
      ],
      contact: {
        title: 'Contact',
        description: 'If you have questions about our privacy policy or wish to exercise your rights, you can contact us:',
        email: 'info@thinklyze.com',
        address: 'Zeytinlik Mah. Fişekhane Cad. Carousel No: 5 İç Kapı No: 17 Bakırköy/İstanbul',
      },
    },
  }

  const pageContent = content[language as 'tr' | 'en' | 'ru'] || content['en']

  return (
    <>
      <Helmet>
        <title>{pageContent.title} - Thinklyze</title>
        <meta name="description" content={language === 'tr' ? 'Thinklyze Gizlilik Politikası - Kişisel verilerinizin korunması ve kullanımı hakkında bilgiler.' : 'Thinklyze Privacy Policy - Information about the protection and use of your personal data.'} />
        <link rel="canonical" href="https://thinklyze.com/privacy" />
      </Helmet>

      <div className="pt-16 bg-white">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {pageContent.title}
            </h1>
            <p className="text-gray-600 text-lg">
              {pageContent.lastUpdated}
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6">
            <div className="space-y-12">
              {pageContent.sections.map((section, index) => {
                const Icon = section.icon
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl border border-gray-100 p-8 md:p-10 shadow-sm hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                          {section.title}
                        </h2>
                        <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {section.content}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Contact Section */}
            <div className="mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-8 md:p-10">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    {pageContent.contact.title}
                  </h2>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {pageContent.contact.description}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <a
                        href={`mailto:${pageContent.contact.email}`}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {pageContent.contact.email}
                      </a>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
                      <p className="text-gray-700">
                        {pageContent.contact.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Privacy















